// src/types/propTypes.js
import PropTypes from 'prop-types';

// Comment display definition
export const CommentDisplayPropType = PropTypes.shape({
  userName: PropTypes.string,
  userFunction: PropTypes.string,
  date: PropTypes.string,
  text: PropTypes.string
});

// Person definition
export const PersonPropType = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  race: PropTypes.string,
  age: PropTypes.number,
  dateOfBirth: PropTypes.string, // ISO date format: YYYY-MM-DD
  gender: PropTypes.string
});

// Judge definition
export const JudgePropType = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string
});

// Attorney definitions
export const AttorneyPropType = PropTypes.shape({
  id: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  barNumber: PropTypes.string
});

export const ProsecutingAttorneyPropType = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string
});

// Sentencing-related PropTypes
export const SentencePropType = PropTypes.shape({
  value: PropTypes.number.isRequired,
  unit: PropTypes.string.isRequired
});

export const FinePropType = PropTypes.shape({
  amount: PropTypes.number.isRequired,
  currency: PropTypes.string
});

export const DismissalPropType = PropTypes.shape({
  reason: PropTypes.string.isRequired,
  date: PropTypes.string
});

export const FugitiveExtraditionPropType = PropTypes.shape({
  date: PropTypes.string,
  destination: PropTypes.string
});

export const ConfinementPropType = PropTypes.shape({
  type: PropTypes.string.isRequired,
  sentence: SentencePropType,
  fine: FinePropType,
  dismissal: DismissalPropType,
  fugitiveExtradition: FugitiveExtraditionPropType,
  facilityType: PropTypes.string,
  comment: PropTypes.string,
  consecutive: PropTypes.bool,
  consecutiveComment: PropTypes.string,
  displayText: PropTypes.string
});

export const ProbationPropType = PropTypes.shape({
  type: PropTypes.string.isRequired,
  sentence: SentencePropType, // Made optional for cases where term is not specified
  status: PropTypes.string,
  isDiversion: PropTypes.bool
});

export const ChargeSentencingPropType = PropTypes.shape({
  confinements: PropTypes.arrayOf(ConfinementPropType),
  probations: PropTypes.arrayOf(ProbationPropType),
  comments: PropTypes.arrayOf(PropTypes.string)
});

// Individual count/charge definition
export const CountPropType = PropTypes.shape({
  countNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  chargeCode: PropTypes.string.isRequired,
  chargeDescription: PropTypes.string.isRequired,
  sentence: SentencePropType, // Legacy field for backward compatibility
  sentencing: ChargeSentencingPropType // New detailed sentencing field
});

// Complete docket/case definition
export const DocketPropType = PropTypes.shape({
  caseNumber: PropTypes.string,
  openDate: PropTypes.string,
  closeDate: PropTypes.string,
  durationDays: PropTypes.number,
  jurisdiction: PropTypes.string,
  courthouse: PropTypes.string,
  departments: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  judges: PropTypes.arrayOf(JudgePropType),
  defendants: PropTypes.arrayOf(PersonPropType),
  defenseAttorneys: PropTypes.arrayOf(AttorneyPropType),
  prosecutingAttorneys: PropTypes.arrayOf(ProsecutingAttorneyPropType),
  counts: PropTypes.arrayOf(CountPropType),
  caseStatus: PropTypes.string,
  comments: PropTypes.arrayOf(CommentDisplayPropType),
  totalConfinementMonths: PropTypes.number,
  totalConfinementDays: PropTypes.number,
  totalProbationMonths: PropTypes.number,
  totalFines: PropTypes.number,
  hasDiversion: PropTypes.bool,
  isDismissed: PropTypes.bool,
  sentencingTypes: PropTypes.arrayOf(PropTypes.string)
}).isRequired;

// Search filters definition
export const DemographicFiltersPropType = PropTypes.shape({
  race: PropTypes.string,
  gender: PropTypes.string,
  minAge: PropTypes.number,
  maxAge: PropTypes.number
});

export const FiltersProps = PropTypes.shape({
  jurisdiction: PropTypes.string,
  courthouse: PropTypes.string,
  departments: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  caseStatus: PropTypes.string,
  judges: PropTypes.arrayOf(PersonPropType),
  defendants: PropTypes.arrayOf(PersonPropType),
  prosecutingAttorneys: PropTypes.arrayOf(PersonPropType),
  defenseAttorneys: PropTypes.arrayOf(PersonPropType),
  charges: PropTypes.arrayOf(PropTypes.string),
  chargeLogic: PropTypes.oneOf(['AND', 'OR']),
  demographics: DemographicFiltersPropType,
  // New sentencing type filters
  sentencingTypes: PropTypes.arrayOf(PropTypes.string),
  // Case type filter (Felony/Misdemeanor)
  caseType: PropTypes.string,
  // Date range filters
  dateFrom: PropTypes.string,
  dateTo: PropTypes.string
});

// Common component props
export const CommonProps = {
  searchResults: PropTypes.arrayOf(DocketPropType),
  onCaseSelect: PropTypes.func,
  onClose: PropTypes.func,
  filters: FiltersProps,
  setFilters: PropTypes.func,
  courts: PropTypes.arrayOf(PropTypes.string)
};

// Search Section props
export const SearchSectionProps = {
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  handleSearchClick: PropTypes.func.isRequired,
  showFilters: PropTypes.bool.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  filters: FiltersProps.isRequired,
  setFilters: PropTypes.func.isRequired,
  courts: PropTypes.arrayOf(PropTypes.string).isRequired
};

export const LocationFiltersProps = PropTypes.shape({
  jurisdiction: PropTypes.string,
  courthouse: PropTypes.string,
  departments: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  jurisdictions: PropTypes.arrayOf(PropTypes.string).isRequired,
  courts: PropTypes.arrayOf(PropTypes.string).isRequired,
  onJurisdictionChange: PropTypes.func.isRequired,
  onCourthouseChange: PropTypes.func.isRequired,
  onDepartmentChange: PropTypes.func.isRequired,
  onRemoveDepartment: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
});
